import type { JSX } from 'react';
import { Button } from '@heroui/react';
import { AlertCircle } from 'lucide-react';

import type { Repository } from '~/types';

const MAX_NAMES_SHOWN = 3;

export interface UnlinkedReposBannerProps {
  /** Callback fired when the user wants to resolve the unlinked repos (opens RepositoriesModal, unlinked-only). */
  onResolve: () => void;
  unlinkedRepos: Repository[];
}

/** Builds "repo-a, repo-b, repo-c + 2 more" from a repo list, capped at MAX_NAMES_SHOWN inline names. */
function formatRepoNames(unlinkedRepos: Repository[]): string {
  const shown = unlinkedRepos.slice(0, MAX_NAMES_SHOWN).map(repo => repo.fullName);
  const remaining = unlinkedRepos.length - shown.length;
  return remaining > 0 ? `${shown.join(', ')} + ${remaining} more` : shown.join(', ');
}

/**
 * Proactive (not error) nudge that some organization repositories aren't
 * attached to any product yet. Props-only — the unlinked set is derived
 * upstream (see useUnlinkedRepositories) and passed in.
 *
 * Pattern: docs/design-patterns/frontend/component-patterns.md (display-components-props-only)
 */
export function UnlinkedReposBanner({
  onResolve,
  unlinkedRepos,
}: UnlinkedReposBannerProps): JSX.Element | null {
  if (unlinkedRepos.length === 0) {
    return null;
  }

  return (
    <div
      className="flex items-center justify-between gap-3 rounded-lg border border-primary/40 bg-primary-100 px-4 py-3 text-sm text-primary-700 dark:bg-primary-200 dark:text-primary-800"
      role="status"
    >
      <p className="flex items-center gap-2">
        <AlertCircle aria-hidden="true" className="h-4 w-4 shrink-0" />
        <span>
          {unlinkedRepos.length} repositor{unlinkedRepos.length !== 1 ? 'ies' : 'y'}{' '}
          {unlinkedRepos.length !== 1 ? "aren't" : "isn't"} linked to a product yet:{' '}
          {formatRepoNames(unlinkedRepos)}
        </span>
      </p>
      <Button color="primary" size="sm" onPress={onResolve}>
        Link Repository
      </Button>
    </div>
  );
}
